import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { addDashboardWidget, deleteWidget, getMe, renderDashboard, updateWidget } from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import type { DashboardWidgetKind, RenderedWidget } from "../lib/types";
import { PageBar } from "../components/Layout";
import { Modal } from "../components/Modal";
import { confirmDialog } from "../components/ConfirmDialog";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";

export function DashboardDetailPage() {
  const { id = "" } = useParams();
  const { data, loading, error, reload } = useApi((signal) => renderDashboard(id, signal), [id]);
  const deleteWidgetMut = useMutation(deleteWidget);
  const [showAddWidget, setShowAddWidget] = useState(false);

  const dashboard = data?.dashboard;
  const widgets = data?.widgets ?? [];

  return (
    <>
      <PageBar
        crumbs={
          <>
            <Link to="/dashboards">Dashboards</Link> <span aria-hidden="true">/</span>{" "}
            {dashboard?.name ?? "Dashboard"}
          </>
        }
        actions={
          <>
            {dashboard ? (
              <button className="btn primary small" onClick={() => setShowAddWidget(true)}>
                + Add widget
              </button>
            ) : null}
            <button className="btn small" onClick={reload} disabled={loading}>
              Refresh
            </button>
          </>
        }
      />
      <div className="content">
        {loading ? (
          <Loading label="Rendering dashboard…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : !dashboard ? (
          <EmptyState>Dashboard not found.</EmptyState>
        ) : (
          <>
            <div className="page-head">
              <div>
                <h1>{dashboard.name}</h1>
                <div className="sub">{widgets.length} widget{widgets.length === 1 ? "" : "s"}</div>
              </div>
            </div>
            {widgets.length === 0 ? (
              <EmptyState>This dashboard has no widgets yet.</EmptyState>
            ) : (
              <div className="widget-grid">
                {widgets.map((w) => (
                  <Widget
                    key={w.id}
                    widget={w}
                    onChanged={reload}
                    onDelete={() =>
                      void confirmDialog({ message: `Delete widget "${w.title || w.kind}"?`, danger: true }).then((yes) => {
                        if (yes) void deleteWidgetMut.run(w.id).then((ok) => ok && reload());
                      })
                    }
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {showAddWidget ? (
        <AddWidgetModal
          dashboardId={id}
          onClose={() => setShowAddWidget(false)}
          onAdded={() => {
            setShowAddWidget(false);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

/** Curated aggregation specs (see WidgetQuerySpec in dashboards-db): only
 *  source/metric/groupBy combos the renderer actually supports. `perm` (when set)
 *  is the permission a non-admin needs to be offered the preset — work-order
 *  widgets require facility.read; ticket widgets ticket.read; tasks are open to
 *  all. The render route still omits widgets the viewer can't see server-side. */
const WIDGET_PRESETS: {
  id: string;
  label: string;
  kind: DashboardWidgetKind;
  title: string;
  spec: object;
  perm?: string;
}[] = [
  {
    id: "tickets-count",
    label: "Ticket count (all tickets)",
    kind: "number",
    title: "Tickets",
    spec: { source: "tickets", metric: "count" },
    perm: "ticket.read",
  },
  {
    id: "tasks-count",
    label: "Task count (all tasks)",
    kind: "number",
    title: "Tasks",
    spec: { source: "tasks", metric: "count" },
  },
  {
    id: "story-points",
    label: "Total story points",
    kind: "number",
    title: "Story points",
    spec: { source: "tasks", metric: "sum_points" },
  },
  {
    id: "tickets-by-status",
    label: "Tickets by status",
    kind: "bar",
    title: "Tickets by status",
    spec: { source: "tickets", metric: "count", groupBy: "status" },
    perm: "ticket.read",
  },
  {
    id: "tasks-by-status",
    label: "Tasks by status",
    kind: "bar",
    title: "Tasks by status",
    spec: { source: "tasks", metric: "count", groupBy: "status" },
  },
  {
    id: "tasks-by-assignee",
    label: "Workload (tasks by assignee)",
    kind: "workload",
    title: "Workload",
    spec: { source: "tasks", metric: "count", groupBy: "assignee_id" },
  },
  {
    id: "points-by-assignee",
    label: "Story points by assignee",
    kind: "bar",
    title: "Points by assignee",
    spec: { source: "tasks", metric: "sum_points", groupBy: "assignee_id" },
  },
  {
    id: "tasks-by-project",
    label: "Tasks by project",
    kind: "pie",
    title: "Tasks by project",
    spec: { source: "tasks", metric: "count", groupBy: "project_id" },
  },
  {
    id: "tickets-by-project",
    label: "Tickets by project",
    kind: "pie",
    title: "Tickets by project",
    spec: { source: "tickets", metric: "count", groupBy: "project_id" },
    perm: "ticket.read",
  },
  {
    id: "work-orders-count",
    label: "Work order count (all work orders)",
    kind: "number",
    title: "Work orders",
    spec: { source: "facility_requests", metric: "count" },
    perm: "facility.read",
  },
  {
    id: "work-orders-by-status",
    label: "Work orders by status",
    kind: "bar",
    title: "Work orders by status",
    spec: { source: "facility_requests", metric: "count", groupBy: "status" },
    perm: "facility.read",
  },
  {
    id: "work-orders-by-assignee",
    label: "Work orders by assignee",
    kind: "bar",
    title: "Work orders by assignee",
    spec: { source: "facility_requests", metric: "count", groupBy: "assignee_id" },
    perm: "facility.read",
  },
];

const WIDGET_KINDS: DashboardWidgetKind[] = [
  "number",
  "bar",
  "line",
  "pie",
  "timeline",
  "workload",
  "table",
];

function AddWidgetModal({
  dashboardId,
  onClose,
  onAdded,
}: {
  dashboardId: string;
  onClose: () => void;
  onAdded: () => void;
}) {
  // Per-type access: only offer presets whose `perm` the user holds (admins see
  // all; until /me loads, fall back to the full list — the render route still
  // omits widgets the viewer can't read server-side). Tasks have no perm.
  const meApi = useApi((signal) => getMe(undefined, signal), []);
  const perms = meApi.data?.permissions;
  const isAdmin = meApi.data?.is_admin === true || meApi.data?.roles?.includes("admin") === true;
  const presets = WIDGET_PRESETS.filter((p) => isAdmin || !perms || !p.perm || perms.includes(p.perm));

  const first = presets[0] ?? WIDGET_PRESETS[0]!;
  const [presetId, setPresetId] = useState(first.id);
  const [title, setTitle] = useState(first.title);
  const [kind, setKind] = useState<DashboardWidgetKind>(first.kind);
  const [advanced, setAdvanced] = useState(false);
  const [jsonText, setJsonText] = useState(JSON.stringify(first.spec, null, 2));
  const [jsonError, setJsonError] = useState<string | null>(null);
  const { run, pending, error } = useMutation(addDashboardWidget);

  function pickPreset(id: string) {
    setPresetId(id);
    const preset = presets.find((p) => p.id === id);
    if (!preset) return;
    setTitle(preset.title);
    setKind(preset.kind);
    setJsonText(JSON.stringify(preset.spec, null, 2));
    setJsonError(null);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    let query: unknown;
    try {
      query = JSON.parse(jsonText);
    } catch {
      setJsonError("Query JSON is not valid JSON.");
      setAdvanced(true);
      return;
    }
    setJsonError(null);
    const res = await run(dashboardId, {
      kind,
      ...(title.trim() ? { title: title.trim() } : {}),
      query_json: query,
    });
    if (res) onAdded();
  }

  return (
    <Modal
      title="Add widget"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button
            className="btn primary"
            type="submit"
            form="add-widget-form"
            disabled={pending}
          >
            {pending ? <Spinner label="Adding…" /> : "Add widget"}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      {jsonError ? <Alert kind="error">{jsonError}</Alert> : null}
      <form id="add-widget-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Data (preset)</span>
          <select value={presetId} onChange={(e) => pickPreset(e.target.value)}>
            {presets.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
          <span className="hint">
            Picking a preset fills in the title, chart kind, and query — all editable below.
          </span>
        </label>

        <div className="row">
          <label className="field">
            <span className="label">Title</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Widget title"
            />
          </label>
          <label className="field">
            <span className="label">Kind</span>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as DashboardWidgetKind)}
            >
              {WIDGET_KINDS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="field">
          <label className="toggle">
            <input
              type="checkbox"
              checked={advanced}
              onChange={(e) => setAdvanced(e.target.checked)}
            />
            Advanced: edit the query JSON
          </label>
        </div>

        {advanced ? (
          <label className="field">
            <span className="label">Query JSON</span>
            <textarea
              value={jsonText}
              onChange={(e) => {
                setJsonText(e.target.value);
                setJsonError(null);
              }}
              rows={7}
              spellCheck={false}
              className="mono"
            />
            <span className="hint">
              Shape: {"{ source: 'tasks'|'tickets'|'facility_requests', metric: 'count'|'sum_points', groupBy?: 'status'|'assignee_id'|'project_id', filters?: { project_id?, status? } }"}
            </span>
          </label>
        ) : null}
      </form>
    </Modal>
  );
}

/** Parse the persisted width (columns, 1–4) out of a widget's layout_json. */
function widgetWidth(layoutJson: string | null | undefined): number {
  try {
    const w = Number((JSON.parse(layoutJson || "{}") as { w?: unknown }).w);
    return Number.isFinite(w) && w >= 1 && w <= 4 ? Math.round(w) : 2;
  } catch {
    return 2;
  }
}

const WIDTH_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: "¼ width" },
  { value: 2, label: "½ width" },
  { value: 3, label: "¾ width" },
  { value: 4, label: "Full width" },
];

/** Render the widget shapes we recognize (number / bar series / table). The
 *  worker's renderer output varies by widget kind; we normalize defensively.
 *  A width selector (persisted in layout_json) sets the widget's column span. */
function Widget({
  widget,
  onDelete,
  onChanged,
}: {
  widget: RenderedWidget;
  onDelete?: () => void;
  onChanged?: () => void;
}) {
  const width = widgetWidth(widget.layout_json);
  const widthMut = useMutation((w: number) => {
    let layout: Record<string, unknown> = {};
    try {
      layout = JSON.parse(widget.layout_json || "{}");
    } catch {
      /* ignore malformed layout */
    }
    return updateWidget(widget.id, { layout_json: { ...layout, w } });
  });

  async function setWidth(w: number) {
    if (w === width) return;
    const res = await widthMut.run(w);
    if (res) onChanged?.();
  }

  return (
    <div className="card widget" style={{ ["--w" as string]: width }}>
      <div className="card-head">
        <h3>{widget.title || widget.kind}</h3>
        <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
          {onChanged ? (
            <select
              className="input-inline"
              value={width}
              onChange={(e) => setWidth(Number(e.target.value))}
              disabled={widthMut.pending}
              aria-label="Widget width"
              title="Widget width"
            >
              {WIDTH_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : null}
          <span className="pill mono">{widget.kind}</span>
          {onDelete ? (
            <button className="btn small danger" onClick={onDelete} title="Delete widget">
              ✕
            </button>
          ) : null}
        </span>
      </div>
      <div className="card-body">
        {widget.error ? (
          <Alert kind="error">{widget.error}</Alert>
        ) : (
          <WidgetBody widget={widget} />
        )}
      </div>
    </div>
  );
}

function asSeries(w: RenderedWidget): { label: string; value: number }[] | null {
  if (Array.isArray(w.series)) return w.series;
  let d: unknown = w.data;
  // Unwrap the common envelope shapes the aggregation API returns, e.g.
  // { buckets: [{key,value}, …] } — otherwise a bar/pie widget falls through to
  // a raw-JSON dump. Also accept { series } / { rows } / { data } wrappers.
  if (d && typeof d === "object" && !Array.isArray(d)) {
    const obj = d as Record<string, unknown>;
    const inner = obj.buckets ?? obj.series ?? obj.rows ?? obj.data;
    if (Array.isArray(inner)) d = inner;
  }
  if (Array.isArray(d) && d.every((r) => r && typeof r === "object")) {
    const rows = d as Record<string, unknown>[];
    // Heuristic: {label,value} or {name,count}-style rows.
    const out = rows
      .map((r) => {
        const label =
          (r.label as string) ?? (r.name as string) ?? (r.key as string) ?? (r.status as string);
        const value = Number(r.value ?? r.count ?? r.total ?? r.n);
        return label != null && Number.isFinite(value) ? { label: String(label), value } : null;
      })
      .filter((x): x is { label: string; value: number } => x !== null);
    return out.length ? out : null;
  }
  return null;
}

function asNumber(w: RenderedWidget): number | null {
  if (typeof w.value === "number") return w.value;
  if (typeof w.data === "number") return w.data;
  if (w.data && typeof w.data === "object" && "value" in (w.data as object)) {
    const v = Number((w.data as { value: unknown }).value);
    return Number.isFinite(v) ? v : null;
  }
  return null;
}

/** Categorical palette for chart segments (color-blind-friendly-ish, distinct
 *  hues). Indexed by segment position. */
const CHART_COLORS = [
  "#2563eb", "#16a34a", "#f59e0b", "#dc2626", "#7c3aed",
  "#0891b2", "#db2777", "#65a30d", "#ea580c", "#475569",
];

/** SVG donut for `pie` widgets: proportional arcs + a center total + a legend
 *  with values and percentages. Pure inline styles (no shared CSS needed). */
function Donut({ series }: { series: { label: string; value: number }[] }) {
  const total = series.reduce((a, s) => a + s.value, 0);
  const R = 56;
  const C = 2 * Math.PI * R;
  const cx = 75;
  const cy = 75;
  const sw = 26;
  let offset = 0;
  return (
    <div style={{ display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap" }}>
      <svg viewBox="0 0 150 150" width={150} height={150} role="img" aria-label="Donut chart">
        <circle cx={cx} cy={cy} r={R} fill="none" stroke="var(--border, #e2e8f0)" strokeWidth={sw} opacity={0.4} />
        {total > 0 &&
          series.map((s, i) => {
            const len = (s.value / total) * C;
            const seg = (
              <circle
                key={`${s.label}-${i}`}
                cx={cx}
                cy={cy}
                r={R}
                fill="none"
                stroke={CHART_COLORS[i % CHART_COLORS.length]}
                strokeWidth={sw}
                strokeDasharray={`${len} ${C - len}`}
                strokeDashoffset={-offset}
                transform={`rotate(-90 ${cx} ${cy})`}
              />
            );
            offset += len;
            return seg;
          })}
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" style={{ fontSize: 22, fontWeight: 700, fill: "var(--text, #0f172a)" }}>
          {total.toLocaleString()}
        </text>
      </svg>
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6, minWidth: 140 }}>
        {series.map((s, i) => (
          <li key={`${s.label}-${i}`} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: CHART_COLORS[i % CHART_COLORS.length], flex: "0 0 auto" }} />
            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.label}</span>
            <span className="num" style={{ fontVariantNumeric: "tabular-nums" }}>
              {s.value}
              <span style={{ color: "var(--muted, #64748b)", marginLeft: 4 }}>
                {total > 0 ? `${Math.round((s.value / total) * 100)}%` : ""}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function WidgetBody({ widget }: { widget: RenderedWidget }) {
  const num = asNumber(widget);
  const series = asSeries(widget);

  if (series && series.length > 0) {
    // Pie/donut kinds get a real donut; everything else gets a ranked bar list.
    if (widget.kind === "pie") {
      return <Donut series={series} />;
    }
    const max = Math.max(1, ...series.map((s) => s.value));
    return (
      <div className="bars">
        {series.map((s, i) => (
          <div className="bar-row" key={`${s.label}-${i}`}>
            <div className="bar-label" title={s.label}>{s.label}</div>
            <div className="bar-track">
              <div
                className="bar-fill"
                style={{ width: `${(s.value / max) * 100}%`, background: CHART_COLORS[i % CHART_COLORS.length] }}
              />
            </div>
            <div className="bar-value num">{s.value.toLocaleString()}</div>
          </div>
        ))}
      </div>
    );
  }

  if (num != null) {
    return <div className="metric">{num.toLocaleString()}</div>;
  }

  if (Array.isArray(widget.rows) && widget.rows.length > 0) {
    const cols = widget.columns ?? Object.keys(widget.rows[0] ?? {});
    return (
      <table className="data">
        <thead>
          <tr>
            {cols.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {widget.rows.map((r, i) => (
            <tr key={i}>
              {cols.map((c) => (
                <td key={c}>{r[c] == null ? "—" : String(r[c])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  // Fallback: show the raw payload so nothing is silently dropped.
  return (
    <pre className="raw-json">{JSON.stringify(widget.data ?? widget, null, 2)}</pre>
  );
}
